import { NotifyLogin, notifyLoginSchema } from '@citibot/models';
import { Box, Grid, Grow, Typography } from '@mui/material';
import citiLogo from 'Assets/CitiBot.png';
import loginBg from 'Assets/hero.png';
import { Auth0Error } from 'auth0-js';
import Button from 'Components/Button';
import Form from 'Components/Forms/Form';
import FormInput from 'Components/Forms/FormInput';
import Timer from 'Components/Timer/Timer';
import { MAXIMUM_LIMIT_ERROR_MESSAGE } from 'constants/common';
import { FormikValues } from 'formik';

import useAuth from 'Hooks/useAuth';
import { useState } from 'react';

const initialValues = {
  email: '',
  code: ''
};

const Login = () => {
  const [isOtpFieldVisible, setOtpFieldVisible] = useState<boolean>(false);
  const [isResendOtpVisible, setIsResendOtpVisible] = useState<boolean>(false);
  const [isTimerExpired, setIsTimerExpired] = useState<boolean>(false);
  const [data, setData] = useState(initialValues);

  // Read the timer value from environment variable or use default
  const RESEND_OTP_TIMER = parseInt(process.env.RESEND_OTP_TIMER || '30', 10);

  const { loginLoading, handleOtpVerify, handlePasswordlessLogin } = useAuth();
  const handleToggleOtpField = () => {
    setOtpFieldVisible(true);
  };

  const handleVerifyError = (email: string, err: Auth0Error | null) => {
    const errCode = err?.code;
    if (errCode === 'too_many_attempts' || err?.description === MAXIMUM_LIMIT_ERROR_MESSAGE) {
      setData({ ...data, code: '' });
      setOtpFieldVisible(false);
    } else if (errCode === 'invalid_user_password') {
      setData({ ...data, code: '' });
    } else {
      // do nothing
    }
  };

  const handleSubmit = async (values: NotifyLogin) => {
    if (values.code) {
      handleOtpVerify(values.email, values.code ?? '', handleVerifyError);
    }
  };

  const handleSendCode = async (values: NotifyLogin) => {
    setData({ ...data, email: values.email });
    setIsResendOtpVisible(true);
    handlePasswordlessLogin(values.email, handleToggleOtpField);
  };

  const handleResendCode = async () => {
    const email = data.email;
    if (email) {
      handleSendCode({ email });
      setIsTimerExpired(false);
    } else {
      console.error('Email is empty');
    }
  };

  const getSubmitButtonLabel = (): string => {
    return isOtpFieldVisible ? 'Submit' : 'Send Code';
  };

  const onFormValuesChange = (values: FormikValues) => {
    setData({ code: values.code, email: values.email });
  };

  const timerExpireCallback = () => {
    setIsTimerExpired(true);
  };

  return (
    <Grid container data-testid="LoginPage" sx={{ height: '100vh' }} spacing={2}>
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          backgroundImage: `url(${loginBg})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: '70%',
          backgroundPosition: 'center'
        }}
      />
      <Grid item xs={12} sm={8} md={5}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
            padding: '10%'
          }}
        >
          <Grid container rowGap={2}>
            <Grid item xs={12}>
              <Box
                component="img"
                sx={{
                  width: '50%'
                }}
                src={citiLogo}
                alt="hero"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h5" sx={{ mb: 2, mt: 5, fontWeight: 500 }}>
                Welcome to CITIBOT NOTIFY
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Form
                enableReinitialize={true}
                initialValues={data}
                onSubmit={isOtpFieldVisible ? handleSubmit : handleSendCode}
                validationSchema={notifyLoginSchema}
                onFormValuesChange={onFormValuesChange}
              >
                <Grid container rowGap={2}>
                  <Grid item xs={12}>
                    <FormInput id="email" name="email" label="Email" />
                  </Grid>
                  {isOtpFieldVisible && (
                    <Grow in={isOtpFieldVisible}>
                      <Grid item xs={12}>
                        <FormInput id="code" label="OTP" />
                        {isResendOtpVisible &&
                          (isTimerExpired ? (
                            <Button
                              variant="text"
                              onClick={handleResendCode}
                              disabled={loginLoading}
                            >
                              Resend OTP
                            </Button>
                          ) : (
                            <Timer
                              timerStartValue={RESEND_OTP_TIMER}
                              text="Resend OTP"
                              timerExpireCallback={timerExpireCallback}
                            />
                          ))}
                      </Grid>
                    </Grow>
                  )}

                  <Grid container item xs={12}>
                    <Button
                      variant="contained"
                      sx={{ mt: 2, mb: 4, borderRadius: 6, px: 8, fontWeight: 500, color: '#fff' }}
                      isLoading={loginLoading}
                      color="secondary"
                      type={'submit'}
                    >
                      {!loginLoading && getSubmitButtonLabel()}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  );
};

export default Login;
